import React, { useState } from 'react';
import { Modal, Row, Col, Typography, Divider, Button, Avatar, Card, Tag, Tooltip } from 'antd';
import { AwardsName } from '../Atoms/AwardsName';
import '../Atoms/finalModal.css';
import NomineeDetailsModal from './NomineeDetailsModal';
import TeamDetailsModal from './TeamDetailsModal';
import { motion } from 'framer-motion';
import { UserOutlined, TeamOutlined, TrophyOutlined, CommentOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Text, Title } = Typography;

interface peersComments {
    assignedToName: string;
    comments: string;
    assignedByEmail: string;
}
// Styled components for enhanced visual appeal
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  .ant-modal-header {
    background: linear-gradient(135deg, #4776E6 0%, #8E54E9 100%);
    border-bottom: none;
    padding: 16px 24px;
    
    .ant-modal-title {
      color: white;
      font-size: 20px;
      font-weight: 600;
    }
  }
`;

const NomineeCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  background: linear-gradient(to right, #f7f7f7, #ffffff);
  
  .ant-card-body {
    padding: 16px;
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledAvatar = styled(Avatar)`
  background: linear-gradient(135deg, #4776E6 0%, #8E54E9 100%);
  font-size: 20px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const TeamAvatar = styled(Avatar)`
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  font-size: 20px;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const CommentCard = styled(Card)`
  border-radius: 8px;
  margin-top: 8px;
  border-left: 4px solid #8E54E9;
  background-color: #f9f9f9;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  }
`;

const StyledTag = styled(Tag)`
  border-radius: 12px;
  padding: 2px 10px;
  margin-left: 8px;
`;

const SectionTitle = styled(Text)`
  position: relative;
  padding-left: 12px;
  font-weight: 600;
  font-size: 16px;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 16px;
    width: 4px;
    background: linear-gradient(135deg, #4776E6 0%, #8E54E9 100%);
    border-radius: 2px;
  }
`;

interface NomineeDetailsModalProps {
    visible: boolean;
    onClose: () => void;
    nominee: {
        name: string;
        title: string;
        department: string;
        badgeNumber: number;
        legalEntityName: string;
        manager: string;
        unit: string;
        managerName: string;
        managerCitation: string;
        firstLevelApprover: string;
        firstLevelCitation: string;
        secondLevelApprover: string;
        secondLevelCitation: string;
        peersComments: peersComments[];
    };
    empdata: any;
}

const FinalModal: React.FC<NomineeDetailsModalProps> = ({ visible, onClose, nominee, empdata }) => {
    const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
    const [formmodal, setformmodal] = useState(false);
    const [teamModal, setTeamModal] = useState<boolean>(false);
    const [teamData, setTeamData] = useState<any[]>([]);
    const [activeSection, setActiveSection] = useState<string>('nominator');

    const getInitials = (name: string) => {
        const names = name.split(' ');
        if (names.length < 2) return name.substring(0, 2).toUpperCase();
        return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
    };

    const handleCancelForm = () => {
        setformmodal(false);
    }

    const formModal = (emploee: any) => {
        setSelectedEmployee(empdata);
        setformmodal(true);
    }

    const handleCancelTeam = () => {
        setTeamModal(false);
    };

    const showTeamMembers = (employee: any) => {
        setTeamModal(true);
        setSelectedEmployee(employee);
        const teamMemberData = employee['teamMembers'];
        const teamMembers = teamMemberData.split("~").filter((item: any) => item.trim() !== '');

        setTeamData(teamMembers.map((member: any) => {
            const emailMatch = member.match(/^([^ ]+)@[^ ]+/);
            const nameMatch = member.match(/\(([^()]+) - /);
            const idMatch = member.match(/- ([^)]+)\)/);

            return {
                name: nameMatch ? nameMatch[1].trim() : '',
                employeeId: idMatch ? idMatch[1].trim() : '',
                email: emailMatch ? emailMatch[0].trim() : '',
            };
        }));
    };
    
    // Animation variants for Framer Motion
    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };
    
    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: "spring",
                stiffness: 100
            }
        }
    };

    return (
        <StyledModal 
            open={visible} 
            onCancel={onClose} 
            footer={null} 
            width={800} 
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrophyOutlined style={{ fontSize: '20px', color: '#FFD700' }} />
                    <span>Nominee Details</span>
                </div>
            }
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <NomineeCard>
                    <AvatarWrapper>
                        {empdata.rewardName === AwardsName.IndividualAwards ? (
                            <>
                                <StyledAvatar size={64}>
                                    {getInitials(empdata.nomineeName)}
                                </StyledAvatar>
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>{empdata.nomineeName}</Title>
                                    <Text type="secondary">{empdata.nomineeDesignation} | {empdata.nomineeUnit}</Text>
                                    <StyledTag color="blue">Individual Award</StyledTag>
                                </div>
                            </>
                        ) : (
                            <>
                                <TeamAvatar size={64} icon={<TeamOutlined />} />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>{empdata.teamName}</Title>
                                    <StyledTag color="green">Team Award</StyledTag>
                                    <div style={{ marginTop: '8px' }}>
                                        <Button 
                                            type="primary" 
                                            icon={<TeamOutlined />}
                                            onClick={() => showTeamMembers(empdata)}
                                        >
                                            View Team Members
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </AvatarWrapper>
                </NomineeCard>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <Button 
                        type={activeSection === 'nominator' ? 'primary' : 'default'} 
                        onClick={() => setActiveSection('nominator')}
                    >
                        Nominator
                    </Button>
                    <Button 
                        type={activeSection === 'manager' ? 'primary' : 'default'} 
                        onClick={() => setActiveSection('manager')}
                    >
                        Manager
                    </Button>
                    <Button 
                        type={activeSection === 'firstLevel' ? 'primary' : 'default'} 
                        onClick={() => setActiveSection('firstLevel')}
                    >
                        First Level
                    </Button>
                    <Button 
                        type={activeSection === 'secondLevel' ? 'primary' : 'default'} 
                        onClick={() => setActiveSection('secondLevel')}
                    >
                        Second Level
                    </Button>
                    {Array.isArray(empdata?.peerComments) && empdata.peerComments.length > 0 && (
                        <Button 
                            type={activeSection === 'peers' ? 'primary' : 'default'} 
                            onClick={() => setActiveSection('peers')}
                        >
                            Reviewers
                        </Button>
                    )}
                </div>
                
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    {activeSection === 'nominator' && (
                        <motion.div variants={itemVariants}>
                            <CommentCard>
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <SectionTitle>Nominator</SectionTitle>
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar icon={<UserOutlined />} />
                                            <Text>{empdata.nominatorName}</Text>
                                        </div>
                                    </Col>
                                    <Col span={16}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <SectionTitle>Nominator's Citation</SectionTitle>
                                            <Button 
                                                type="primary" 
                                                icon={<CommentOutlined />} 
                                                onClick={() => formModal(empdata)}
                                            >
                                                View Citation
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </CommentCard>
                        </motion.div>
                    )}
                    
                    {activeSection === 'manager' && (
                        <motion.div variants={itemVariants}>
                            <CommentCard>
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <SectionTitle>Manager</SectionTitle>
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar icon={<UserOutlined />} />
                                            <Text>{empdata.managerName}</Text>
                                        </div>
                                    </Col>
                                    <Col span={16}>
                                        <SectionTitle>Manager's Citation</SectionTitle>
                                        <div style={{ marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '8px' }}>
                                            <Text>{empdata.managerComments}</Text>
                                        </div>
                                    </Col>
                                </Row>
                            </CommentCard>
                        </motion.div>
                    )}

                    {activeSection === 'firstLevel' && (
                        <motion.div variants={itemVariants}>
                            <CommentCard>
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <SectionTitle>First Level Approver</SectionTitle>
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar icon={<UserOutlined />} />
                                            <Text>{empdata.firstLevelApproverName}</Text>
                                        </div>
                                    </Col>
                                    <Col span={16}>
                                        <SectionTitle>First Level Approver's Citation</SectionTitle>
                                        <div style={{ marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '8px' }}>
                                            <Text>{empdata.firstLevelApproverComments}</Text>
                                        </div>
                                    </Col>
                                </Row>
                            </CommentCard>
                        </motion.div>
                    )}

                    {activeSection === 'secondLevel' && (
                        <motion.div variants={itemVariants}>
                            <CommentCard>
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <SectionTitle>Second Level Approver</SectionTitle>
                                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Avatar icon={<UserOutlined />} />
                                            <Text>{empdata.secondLevelApproverName}</Text>
                                        </div>
                                    </Col>
                                    <Col span={16}>
                                        <SectionTitle>Second Level Approver's Citation</SectionTitle>
                                        <div style={{ marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '8px' }}>
                                            <Text>{empdata.secondLevelApproverComments}</Text>
                                        </div>
                                    </Col>
                                </Row>
                            </CommentCard>
                        </motion.div>
                    )}
                    
                    {activeSection === 'peers' && Array.isArray(empdata?.peerComments) && empdata.peerComments.length > 0 && (
                        <motion.div variants={containerVariants}>
                            <SectionTitle>Additional Reviewers</SectionTitle>
                            {empdata.peerComments.map((peer: peersComments, idx: number) => (
                                <motion.div key={idx} variants={itemVariants}>
                                    <CommentCard style={{ marginTop: '12px' }}>
                                        <Row gutter={[16, 16]}>
                                            <Col span={8}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Avatar icon={<UserOutlined />} />
                                                    <Text>{peer.assignedToName}</Text>
                                                </div>
                                            </Col>
                                            <Col span={16}>
                                                <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '8px' }}>
                                                    <Text>{peer.comments}</Text>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CommentCard>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </motion.div>
            </motion.div>

            <NomineeDetailsModal
                visible={formmodal}
                onCancel={handleCancelForm}
                empData={selectedEmployee}
            />

            <TeamDetailsModal
                visible={teamModal}
                onCancel={handleCancelTeam}
                empData={empdata}
                teamData={teamData}
            />
        </StyledModal>
    );
};

export default FinalModal;
